import { Controller } from "@hotwired/stimulus"
import { FileUploader, type UploadedFile, type RestoredFile } from "../utils/file_uploader"
import {
  LucideIconCache,
  X,
  AlertCircle,
  Loader,
  CheckCircle,
  type IconNode,
} from "../utils/lucide_icon_cache"
import { FileText, Image, Table, File, createElement } from "lucide"

type FileStatus = UploadedFile["status"]

interface StatusConfig {
  className: string
  icon: IconNode | null
}

const FILE_STATUS_CONFIG: Record<FileStatus, StatusConfig> = {
  error: { className: "is-error", icon: AlertCircle },
  complete: { className: "is-complete", icon: CheckCircle },
  uploading: { className: "is-uploading", icon: Loader },
  deleting: { className: "is-deleting", icon: Loader },
  pending: { className: "", icon: null },
}

// File type icon mapping based on MIME type
function getFileTypeIcon(fileName: string): IconNode {
  const extension = fileName.split(".").pop()?.toLowerCase() || ""

  // PDF files
  if (extension === "pdf") {
    return FileText
  }

  // Image files
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(extension)) {
    return Image
  }

  // Spreadsheet files
  if (["xlsx", "xls", "csv", "tsv"].includes(extension)) {
    return Table
  }

  // Document files
  if (["doc", "docx", "txt", "rtf"].includes(extension)) {
    return FileText
  }

  // Default file icon
  return File
}

export default class FileDropzoneController extends Controller {
  static targets = ["dropzone", "input", "fileList", "error", "clickarea", "previousUpload"]
  static values = {
    endpoint: String,
    maxFiles: { type: Number, default: 3 },
    maxFileSize: { type: Number, default: 5242880 },
    allowedTypes: Array,
    requireUpload: { type: Boolean, default: false },
  }

  declare dropzoneTarget: HTMLDivElement
  declare inputTarget: HTMLInputElement
  declare fileListTarget: HTMLDivElement
  declare errorTarget: HTMLDivElement
  declare clickareaTarget: HTMLDivElement
  declare previousUploadTargets: HTMLDivElement[]
  declare hasPreviousUploadTarget: boolean

  declare endpointValue: string
  declare maxFilesValue: number
  declare maxFileSizeValue: number
  declare allowedTypesValue: string[]
  declare requireUploadValue: boolean

  private uploader: FileUploader | null = null

  connect() {
    this.uploader = new FileUploader({
      target: this.dropzoneTarget,
      endpoint: this.endpointValue,
      maxFiles: this.maxFilesValue,
      maxFileSize: this.maxFileSizeValue,
      allowedTypes: this.allowedTypesValue,
      onStateChange: (files) => this.renderFileList(files),
      onError: (message) => this.showError(message),
      onDeleteError: (_fileId, fileName) => this.handleDeleteError(fileName),
    })

    this.restorePreviousUploads()
  }

  disconnect() {
    this.uploader?.destroy()
  }

  // Actions

  browse() {
    if (this.uploader?.canAddFiles()) {
      this.inputTarget.click()
    }
  }

  browseOnKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.browse()
    }
  }

  selectFiles(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files) {
      this.uploader?.addFiles(input.files)
    }
    input.value = ""
  }

  // Private

  private restorePreviousUploads() {
    if (!this.hasPreviousUploadTarget) return

    const files: RestoredFile[] = []
    this.previousUploadTargets.forEach((el) => {
      const { name, contentType, signedId, size } = el.dataset
      if (!name || !signedId) return
      files.push({
        name,
        contentType,
        signedId,
        size: size ? parseInt(size, 10) : undefined,
      })
    })

    this.uploader?.restoreFiles(files)
  }

  private renderFileList(files: UploadedFile[]) {
    const atMax = files.length >= this.maxFilesValue
    const uploading = files.some((f) => f.status === "uploading")
    const hasComplete = files.some((f) => f.status === "complete")

    // Disable clickarea when at max files
    this.clickareaTarget.classList.toggle("is-disabled", atMax)

    this.fileListTarget.replaceChildren(...files.map((file) => this.renderFileChip(file)))

    this.dispatchStateEvent(uploading, hasComplete)
  }

  private renderFileChip(file: UploadedFile): HTMLElement {
    const config = FILE_STATUS_CONFIG[file.status]
    const statusClass = config.className
    const isUploading = file.status === "uploading"
    const isDeleting = file.status === "deleting"

    // Create chip container
    const chip = document.createElement("div")
    chip.className = `file-chip-dropzone ${statusClass}`
    chip.setAttribute("role", "listitem")
    chip.setAttribute("aria-label", `File: ${file.name}`)
    if (isDeleting) {
      chip.setAttribute("aria-busy", "true")
    }
    chip.addEventListener("click", (e) => e.stopPropagation())

    // File type icon
    const iconWrapper = document.createElement("span")
    iconWrapper.className = "file-chip-dropzone-icon"
    const fileTypeIcon = getFileTypeIcon(file.name)
    iconWrapper.innerHTML = this.createIconSvg(fileTypeIcon, 16)
    chip.appendChild(iconWrapper)

    // File name
    const fileName = document.createElement("span")
    fileName.className = "file-chip-dropzone-name"
    fileName.textContent = file.name
    fileName.title = file.name
    chip.appendChild(fileName)

    // Progress bar (only during upload)
    if (isUploading) {
      const progressContainer = document.createElement("div")
      progressContainer.className = "file-chip-dropzone-progress"

      const progressBar = document.createElement("div")
      progressBar.className = "file-chip-dropzone-progress-bar"
      progressBar.style.width = `${file.progress}%`
      progressContainer.appendChild(progressBar)

      chip.appendChild(progressContainer)
    }

    // Status icon (spinner, checkmark, or error)
    if (config.icon) {
      const status = document.createElement("span")
      status.className = "file-chip-dropzone-status"
      status.setAttribute("role", "status")
      status.innerHTML = LucideIconCache.get(config.icon, 14)

      // Add visually-hidden text for screen readers on complete state
      if (file.status === "complete") {
        const srText = document.createElement("span")
        srText.className = "visually-hidden"
        srText.textContent = `${file.name} upload complete`
        status.appendChild(srText)
      }

      chip.appendChild(status)
    }

    // Remove button
    const removeButton = document.createElement("button")
    removeButton.type = "button"
    removeButton.className = "file-chip-dropzone-remove"
    removeButton.setAttribute("aria-label", `Remove ${file.name}`)
    removeButton.innerHTML = LucideIconCache.get(X, 14)
    if (isDeleting) {
      removeButton.disabled = true
    } else {
      removeButton.addEventListener("click", (e) => {
        e.stopPropagation()
        this.uploader?.removeFile(file.id)
      })
    }
    chip.appendChild(removeButton)

    return chip
  }

  // Create SVG for file type icons (not in cache)
  private createIconSvg(icon: IconNode, size: number): string {
    const svg = createElement(icon, { width: size, height: size })
    return svg.outerHTML
  }

  private showError(message: string) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
    setTimeout(() => this.errorTarget.classList.add("d-none"), 5000)
  }

  private dispatchStateEvent(uploading: boolean, hasComplete: boolean) {
    window.dispatchEvent(
      new CustomEvent("file-dropzone:stateChanged", {
        detail: { uploading, hasComplete, requireUpload: this.requireUploadValue },
      })
    )
  }

  private handleDeleteError(fileName: string) {
    this.showError(`Couldn't remove ${fileName}. Please try again.`)
  }
}
